import { useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  Boxes,
  Clock,
  Globe,
  HandCoins,
  Menu,
  ShieldCheck,
  Smartphone,
  X,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useBrand } from '../../brand/BrandProvider';

/**
 * Reseller-branded landing — §5.2 / §5.3
 * Reads the host-resolved brand (useBrand, Decision #21) for name/colour/support,
 * so it renders correctly straight from the brand resolved in SessionGate — no
 * separate reseller fetch, no ITCity/Possignin leak on a reseller host.
 */
export function Landing() {
  const r = useBrand();
  const accent = r.primaryColor;
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark">
      {/* Header */}
      <header className="border-b border-zinc-200 dark:border-zinc-700">
        <div className="px-4 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span
              className="w-9 h-9 rounded-lg grid place-items-center text-white font-semibold text-base shadow-sm"
              style={{ backgroundColor: accent }}
              aria-hidden="true"
            >
              {r.name.charAt(0)}
            </span>
            <span className="font-semibold text-base tracking-tight text-zinc-950 dark:text-zinc-50">
              {r.name}
            </span>
          </div>

          <nav className="hidden sm:flex items-center gap-7 text-sm">
            <a
              href="#features"
              className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-50 transition"
            >
              Features
            </a>
            <Link
              to="/pricing"
              className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-50 transition"
            >
              Pricing
            </Link>
            <a
              href={`mailto:${r.supportEmail}`}
              className="text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-50 transition"
            >
              Contact
            </a>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/login"
              className="hidden sm:inline-block text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:text-zinc-950 dark:hover:text-zinc-50"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium text-white shadow-xs transition hover:opacity-90"
              style={{ backgroundColor: accent }}
            >
              Sign up
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button
              type="button"
              onClick={() => setOpen((s) => !s)}
              className="sm:hidden w-10 h-10 -mr-1 grid place-items-center rounded-lg text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="sm:hidden border-t border-zinc-200 dark:border-zinc-700 px-4 py-3 space-y-1">
            <a
              href="#features"
              onClick={() => setOpen(false)}
              className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            >
              Features
            </a>
            <Link
              to="/pricing"
              onClick={() => setOpen(false)}
              className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            >
              Pricing
            </Link>
            <a
              href={`mailto:${r.supportEmail}`}
              onClick={() => setOpen(false)}
              className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            >
              Contact
            </a>
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              className="block px-3 py-3 rounded-lg text-[15px] font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            >
              Login
            </Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="glow-bg absolute inset-0 pointer-events-none" aria-hidden="true" />

        <div className="relative px-4 sm:px-6 py-16 sm:py-24 max-w-6xl mx-auto text-center">
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-6"
            style={{
              backgroundColor: `${accent}1a`,
              color: accent,
            }}
          >
            Built for Bangladesh
          </span>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-[1.05] max-w-4xl mx-auto">
            Run your shop with {r.name}
            <br />
            <span className="text-zinc-500 dark:text-zinc-500">
              — built for Bangladesh.
            </span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto">
            Cloud POS with HRM, CRM, manufacturing, repair, and WooCommerce sync. Pay in BDT,
            get cash or SSLCommerz. Built for shops, restaurants, pharmacies, and workshops.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-white shadow-sm transition hover:opacity-90 w-full sm:w-auto"
              style={{ backgroundColor: accent }}
            >
              Start free trial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-zinc-900 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition w-full sm:w-auto backdrop-blur-sm"
            >
              Login
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <ul className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-zinc-600 dark:text-zinc-300">
            <Tick>7-day trial</Tick>
            <Tick>Cash to office in Sylhet</Tick>
            <Tick>Setup in 5 min</Tick>
            <Tick>Bangla + English UI</Tick>
          </ul>
        </div>
      </section>

      {/* Features */}
      <section
        id="features"
        className="px-4 sm:px-6 py-16 sm:py-20 max-w-6xl mx-auto"
      >
        <div className="text-center mb-12">
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Why {r.name}
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Everything your shop needs.
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          <Feature
            icon={Boxes}
            accent={accent}
            title="Sales & inventory"
            body="Track every sale and every item. FIFO, lot tracking, and barcode scanning out of the box."
          />
          <Feature
            icon={HandCoins}
            accent={accent}
            title="BDT-native billing"
            body="SSLCommerz built in. Cash to office. Auto invoices and receipts in BDT."
          />
          <Feature
            icon={BarChart3}
            accent={accent}
            title="Reports that read"
            body="Daily sales, P&L, stock alerts. Export to CSV any time."
          />
          <Feature
            icon={Smartphone}
            accent={accent}
            title="Works on a phone"
            body="POS that runs on Android, iPhone, or any browser. Receipt printers supported."
          />
          <Feature
            icon={Globe}
            accent={accent}
            title="Bangla + English"
            body="Switch language per user. Receipts and reports follow."
          />
          <Feature
            icon={ShieldCheck}
            accent={accent}
            title="Calm and secure"
            body="2FA, audit log, role-based access. Your data stays in your database — we never sell it."
          />
        </div>
      </section>

      {/* CTA strip */}
      <section className="px-4 sm:px-6 py-14 max-w-6xl mx-auto">
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
            Ready to get started?
          </h2>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 max-w-md mx-auto">
            Free for 7 days, no card needed. Setup takes 5 minutes.
          </p>
          <Link
            to="/signup"
            className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-base font-medium text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            Start free trial
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer — fully white-label: the reseller's own name/domain/support only
          (Decision #21 / SECURITY_COMPLIANCE_CHECKLIST §22; no ITCity/Possignin). */}
      <footer className="px-4 sm:px-8 py-8 border-t border-zinc-200 dark:border-zinc-700">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5 tabular-nums">
            <Clock className="w-3.5 h-3.5" />
            © 2026 {r.name}
          </p>
          <div className="flex items-center gap-4 text-xs text-zinc-500 dark:text-zinc-400">
            <Link to="/terms" className="hover:text-zinc-700 dark:hover:text-zinc-200">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-zinc-700 dark:hover:text-zinc-200">
              Privacy
            </Link>
            <a
              href={`mailto:${r.supportEmail}`}
              className="hover:text-zinc-700 dark:hover:text-zinc-200 mono"
            >
              {r.supportEmail}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Tick({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
      {children}
    </li>
  );
}

function Feature({
  icon: Icon,
  accent,
  title,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  accent: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-950 p-5 sm:p-6 hover:shadow-sm transition">
      <span
        className="w-10 h-10 grid place-items-center rounded-lg text-white"
        style={{ backgroundColor: accent }}
      >
        <Icon className="w-5 h-5" />
      </span>
      <h3 className="mt-4 text-base font-semibold text-zinc-950 dark:text-zinc-50">
        {title}
      </h3>
      <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
        {body}
      </p>
    </div>
  );
}
